import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { IMarkerTypeAll, IValuation, IPagination } from '../models';
import { GeomarkerEndpoint, UserEndpoint } from '../endpoints';
import { StringUtil } from '@shared/utils';

/**
 * ### **ProfileService**
 *
 * Un Servicio en Angular es el mecanismo para compartir funcionalidad entre componentes, en este caso se encarga de de compartir las funciones
 * hacen diferentes peticiones o llamados a distintos API's que genera el backend.Algunos componentes que hacen uso de este servicio son:
 *
 *  * {@link ProfileUserActivitiesComponent}
 *
 *  * {@link ProfileActivitiesFilterComponent}
 */
@Injectable({
  providedIn: 'root',
})
export class Profile {
  private readonly http = inject(HttpClient);

  /** @ignore */
  constructor() {}

  /**
   * Función encargada de obtener las categoria padre de los geomarcadores, para esto hace una peticion de tipo GET al servidor.
   * @returns Observable con las categorias padre de los geomarcadores.
   */
  getCategories(): Observable<Array<IMarkerTypeAll>> {
    return this.http.get<Array<IMarkerTypeAll>>(GeomarkerEndpoint.categories);
  }

  /**
   * Función encargada de obtener los comentarios o valoraciones realizados por un usuario a una o varias ciudades, para esto hace
   * una peticion de tipo GET al servidor.
   * @param id Parametro que hace referencia al id del usuario.
   * @param city Parametro que hace referencia a la ciudad(es).
   */
  getComments(id: any, city: string): Observable<IPagination<Array<IValuation>>> {
    return this.http.get<IPagination<Array<IValuation>>>(
      StringUtil.format(UserEndpoint.commentsUserCity, id, city)
    );
  }

  /**
   * Función encargada de obtener las calificaciones realizadas por un usuario a una o varias ciudades, para esto hace
   * una peticion de tipo GET al servidor.
   * @param id Parametro que hace referencia al id del usuario.
   * @param city Parametro que hace referencia a la ciudad(es).
   */
  getQualificationCity(user: number, city: string): Observable<IPagination<Array<IValuation>>> {
    return this.http.get<IPagination<Array<IValuation>>>(
      StringUtil.format(UserEndpoint.qualificationUserCity, user, city)
    );
  }

  /**
   * Función encargada de obtener las calificaciones realizadas por un usuario a uno o varios geomarcadores, para esto hace
   * una peticion de tipo GET al servidor.
   * @param id Parametro que hace referencia al id del usuario.
   * @param marker Parametro que hace referencia a la categoria del geomarcador.
   */
  getQualificationMaker(id: any, marker: any): Observable<IPagination<Array<IValuation>>> {
    return this.http.get<IPagination<Array<IValuation>>>(
      StringUtil.format(GeomarkerEndpoint.qualificationUserMaker, id, marker)
    );
  }

  /**
   * Función encargada de obtener las valoraciones o comentarios realizados por un usuario a uno o varios geomarcadores, para esto hace
   * una peticion de tipo GET al servidor.
   * @param id Parametro que hace referencia al id del usuario.
   * @param marker Parametro que hace referencia a la categoria del geomarcador.
   */
  getvaluationMaker(id: any, marker: any): Observable<IPagination<Array<IValuation>>> {
    return this.http.get<IPagination<Array<IValuation>>>(
      StringUtil.format(GeomarkerEndpoint.ValuationsUserMaker, id, marker)
    );
  }

  /**
   * Función encargada de obtener las calificaciones realizadas por un usuario a ciudades o geomarcadores, para esto hace
   * una peticion de tipo GET al servidor.
   * @param id Parametro que hace referencia al id del usuario.
   * @param query Parametro que hace referencia a un geomarcador o una ciudad.
   */
  getQualificationSearch(id: any, query: string): Observable<IPagination<Array<IValuation>>> {
    return this.http.get<IPagination<Array<IValuation>>>(
      StringUtil.format(UserEndpoint.qualificationSearch, id, query)
    );
  }

  /**
   * Función encargada de obtener las valoraciones o comentarios realizados por un usuario a ciudades o geomarcadores, para esto hace
   * una peticion de tipo GET al servidor.
   * @param id Parametro que hace referencia al id del usuario.
   * @param query Parametro que hace referencia a un geomarcador o una ciudad.
   */
  getValorationSearch(id: any, query: string): Observable<IPagination<Array<IValuation>>> {
    return this.http.get<IPagination<Array<IValuation>>>(
      StringUtil.format(UserEndpoint.valorationSearch, id, query)
    );
  }

  /**
   * Function responsible for clearing the cache.
   * Makes a POST request to the corresponding endpoint to invalidate the cache.
   */
  clearCachePost(): Observable<any> {
    return this.http.post(UserEndpoint.clearCache, {});
  }
}
